import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router';
import { toast } from 'react-toastify';
import Button from '../../components/BaseComponents/Button';
import Loader from '../../components/BaseComponents/Loader';
import Modal from '../../components/BaseComponents/Modal';
import TextArea from '../../components/BaseComponents/TextArea';
import TextInput from '../../components/BaseComponents/TextInput';
import {
  CmsDocument,
  CmsPackage,
  CmsPageDetail,
  CmsPageSummary,
  CmsTransition,
  cmsErrorMessage,
  createCmsPackage,
  createCmsPage,
  deleteCmsPackage,
  deleteCmsPage,
  getCmsPage,
  listCmsPackages,
  listCmsPages,
  restoreCmsRevision,
  saveCmsPage,
  transitionCmsPage,
  updateCmsPackage,
} from '../../api/marketingCms';
import { RootState } from '../../store/store';
import { UserRoleEnum } from '../../types/apiGeneratedTypes';
import EditorialCms from './EditorialCms';
import './MarketingCmsPage.scss';

const isSuperAdminUser = (admin: RootState['admin']['admin']) =>
  admin.role === UserRoleEnum.SuperAdmin && admin.is_super_admin === true;

const EMPTY_DOCUMENT: CmsDocument = {
  schema_version: 1,
  blocks: [{ id: 'intro', type: 'paragraph', text: 'Add the page content.' }],
};

const ACTION_LABELS: Record<string, string> = {
  start_edit: 'Edit live page',
  submit: 'Submit for review',
  request_changes: 'Request changes',
  publish: 'Publish',
  unpublish: 'Unpublish',
  discard: 'Discard edit',
  delete: 'Delete draft',
};

const CONFIRM_ACTIONS = new Set(['publish', 'unpublish', 'discard', 'delete']);

const statusLabel = (status: string, workflow: string) => {
  if (status === 'published' && workflow === 'none') return 'Live';
  if (status === 'published' && workflow === 'draft') return 'Live · editing';
  if (status === 'published' && workflow === 'in_review') return 'Live · in review';
  if (status === 'published' && workflow === 'changes_requested') {
    return 'Live · changes requested';
  }
  if (workflow === 'in_review') return 'In review';
  if (workflow === 'changes_requested') return 'Changes requested';
  return 'Draft';
};

const MarketingCmsPage = () => {
  const admin = useSelector((state: RootState) => state.admin.admin);
  const [tab, setTab] = useState<'pages' | 'packages' | 'topics' | 'posts' | 'shared'>('topics');
  const [pages, setPages] = useState<CmsPageSummary[]>([]);
  const [packages, setPackages] = useState<CmsPackage[]>([]);
  const [detail, setDetail] = useState<CmsPageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [blocksText, setBlocksText] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [packageOpen, setPackageOpen] = useState(false);
  const [packageEditing, setPackageEditing] = useState<CmsPackage | null>(null);

  const editable = detail?.actions.includes('submit') ?? false;

  const applyDetail = (page: CmsPageDetail) => {
    setDetail(page);
    setTitle(page.title);
    setMetaTitle(page.meta_title);
    setMetaDescription(page.meta_description);
    setBlocksText(JSON.stringify(page.content, null, 2));
    setReturnReason('');
    setNotice(null);
    setPages((current) =>
      current.map((item) =>
        item.slug === page.slug
          ? {
              ...item,
              title: page.title,
              status: page.status,
              workflow_status: page.workflow_status,
            }
          : item
      )
    );
  };

  const load = async (slug?: string) => {
    setLoading(true);
    try {
      const [nextPages, nextPackages] = await Promise.all([
        listCmsPages(),
        listCmsPackages(),
      ]);
      setPages(nextPages);
      setPackages(nextPackages);
      const nextSlug = slug ?? detail?.slug ?? nextPages[0]?.slug;
      if (nextSlug) {
        applyDetail(await getCmsPage(nextSlug));
      }
    } catch (error) {
      toast.error(cmsErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdminUser(admin)) {
      void load();
    }
    // The page list is loaded once for this admin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);

  if (!isSuperAdminUser(admin)) {
    return <Navigate to='/members' replace />;
  }

  const selectPage = async (slug: string) => {
    setSaving(true);
    try {
      applyDetail(await getCmsPage(slug));
    } catch (error) {
      toast.error(cmsErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const parseDocument = (): CmsDocument | null => {
    try {
      const parsed: unknown = JSON.parse(blocksText);
      if (
        typeof parsed !== 'object' ||
        parsed === null ||
        !('blocks' in parsed)
      ) {
        setNotice('Page blocks must be a JSON document with a blocks array.');
        return null;
      }
      return parsed as CmsDocument;
    } catch {
      setNotice('Page blocks must be valid JSON.');
      return null;
    }
  };

  const save = async () => {
    if (!detail) return;
    const content = parseDocument();
    if (!content) return;
    setSaving(true);
    try {
      applyDetail(
        await saveCmsPage(detail.slug, {
          title,
          meta_title: metaTitle,
          meta_description: metaDescription,
          content,
        })
      );
      toast.success('Draft saved');
    } catch (error) {
      setNotice(cmsErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (action: string) => {
    if (!detail) return;
    if (CONFIRM_ACTIONS.has(action)) {
      const label = ACTION_LABELS[action] ?? action;
      if (!window.confirm(`${label}?`)) return;
    }
    if (action === 'request_changes' && returnReason.trim().length === 0) {
      setNotice('A reason is required when requesting changes.');
      return;
    }
    setSaving(true);
    try {
      if (action === 'delete') {
        await deleteCmsPage(detail.slug);
        setDetail(null);
        toast.success('Draft deleted');
        await load();
        return;
      }
      applyDetail(
        await transitionCmsPage(
          detail.slug,
          action as CmsTransition,
          action === 'request_changes' ? returnReason.trim() : undefined
        )
      );
      toast.success(ACTION_LABELS[action] ?? 'Updated');
    } catch (error) {
      setNotice(cmsErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const restore = async (revisionId: number, revisionNumber: number) => {
    if (!detail || !window.confirm(`Restore revision ${revisionNumber} into the open edit?`)) {
      return;
    }
    setSaving(true);
    try {
      applyDetail(await restoreCmsRevision(detail.slug, revisionId));
      toast.success(`Restored revision ${revisionNumber}`);
    } catch (error) {
      setNotice(cmsErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const openPackage = (item: CmsPackage | null) => {
    setPackageEditing(item);
    setPackageOpen(true);
  };

  const removePackage = async (item: CmsPackage) => {
    if (item.referenced_slugs.length > 0) {
      toast.error(`Package is used by: ${item.referenced_slugs.join(', ')}`);
      return;
    }
    if (!window.confirm(`Delete ${item.name}?`)) return;
    try {
      await deleteCmsPackage(item.key);
      toast.success('Package deleted');
      await load(detail?.slug);
    } catch (error) {
      toast.error(cmsErrorMessage(error));
    }
  };

  return (
    <div className='cms-page'>
      <div className='cms-header'>
        <div>
          <h1>Website CMS</h1>
          <p>
            AI generation is open. Pick a topic, read the draft below, then
            regenerate it. Publish from Posts before it appears on the public site.
          </p>
        </div>
        {(tab === 'pages' || tab === 'packages') ? (
          <Button
            text={tab === 'pages' ? 'New page' : 'New package'}
            onClick={() => (tab === 'pages' ? setCreateOpen(true) : openPackage(null))}
          />
        ) : null}
      </div>
      <div className='cms-tabs'>
        <button
          type='button'
          className={tab === 'topics' ? 'cms-tab active' : 'cms-tab'}
          onClick={() => setTab('topics')}
        >
          AI generation
        </button>
        <button
          type='button'
          className={tab === 'pages' ? 'cms-tab active' : 'cms-tab'}
          onClick={() => setTab('pages')}
        >
          Pages
        </button>
        <button
          type='button'
          className={tab === 'packages' ? 'cms-tab active' : 'cms-tab'}
          onClick={() => setTab('packages')}
        >
          Packages
        </button>
        <button
          type='button'
          className={tab === 'posts' ? 'cms-tab active' : 'cms-tab'}
          onClick={() => setTab('posts')}
        >
          Posts
        </button>
        <button
          type='button'
          className={tab === 'shared' ? 'cms-tab active' : 'cms-tab'}
          onClick={() => setTab('shared')}
        >
          Shared data
        </button>
      </div>
      {loading ? <Loader /> : null}
      {!loading && tab === 'pages' ? (
        <div className='cms-layout'>
          <div className='cms-list'>
            {pages.map((page) => (
              <button
                type='button'
                key={page.slug}
                className={detail?.slug === page.slug ? 'active' : ''}
                onClick={() => void selectPage(page.slug)}
              >
                <strong>{page.title}</strong>
                <span className='cms-meta'>
                  /{page.slug}/ · {statusLabel(page.status, page.workflow_status)}
                </span>
              </button>
            ))}
          </div>
          {detail ? (
            <div className='cms-editor'>
              <div className='cms-actions'>
                <strong>{statusLabel(detail.status, detail.workflow_status)}</strong>
                {detail.actions
                  .filter((action) => action !== 'delete')
                  .map((action) => (
                    <Button
                      key={action}
                      text={ACTION_LABELS[action] ?? action}
                      variant={action === 'unpublish' || action === 'discard' ? 'delete' : undefined}
                      isDisabled={saving}
                      onClick={() => void runAction(action)}
                    />
                  ))}
                {detail.actions.includes('delete') ? (
                  <Button
                    text='Delete draft'
                    variant='delete'
                    isDisabled={saving}
                    onClick={() => void runAction('delete')}
                  />
                ) : null}
              </div>
              {detail.status === 'published' && detail.workflow_status !== 'none' ? (
                <div className='cms-banner'>
                  The live page is still public. This edit is hidden until you publish it.
                </div>
              ) : null}
              {detail.status === 'draft' ? (
                <div className='cms-banner'>This page is not on the live site.</div>
              ) : null}
              {detail.return_reason ? (
                <div className='cms-banner'>Changes requested: {detail.return_reason}</div>
              ) : null}
              {notice ? <div className='cms-banner error'>{notice}</div> : null}
              <div className='cms-fields'>
                <TextInput
                  name='title'
                  title='Title'
                  value={title}
                  isDisabled={!editable || saving}
                  onChange={(event) => setTitle(event.target.value)}
                />
                <TextInput
                  name='meta_title'
                  title='Meta title'
                  value={metaTitle}
                  isDisabled={!editable || saving}
                  onChange={(event) => setMetaTitle(event.target.value)}
                />
                <TextArea
                  name='meta_description'
                  title='Meta description'
                  value={metaDescription}
                  isDisabled={!editable || saving}
                  onChange={(event) => setMetaDescription(event.target.value)}
                />
                <label>
                  <span className='cms-meta'>
                    Blocks. Types: heading, paragraph, list, package_card.
                  </span>
                  <textarea
                    className='cms-json'
                    rows={16}
                    value={blocksText}
                    disabled={!editable || saving}
                    onChange={(event) => setBlocksText(event.target.value)}
                  />
                </label>
                {editable ? (
                  <Button text='Save draft' isDisabled={saving} onClick={() => void save()} />
                ) : null}
                {detail.actions.includes('request_changes') ? (
                  <TextArea
                    name='return_reason'
                    title='Reason for changes'
                    value={returnReason}
                    onChange={(event) => setReturnReason(event.target.value)}
                  />
                ) : null}
              </div>
              <div className='cms-revisions'>
                <strong>Revisions</strong>
                {detail.revisions.map((revision) => (
                  <div className='cms-revision-row' key={revision.id}>
                    <span className='cms-meta'>
                      {revision.revision_number}. {revision.change_kind.replace(/_/g, ' ')}
                      {revision.changed_by ? ` · ${revision.changed_by}` : ''}
                      {revision.change_note ? ` · ${revision.change_note}` : ''}
                    </span>
                    {detail.can_restore ? (
                      <Button
                        text='Restore'
                        isDisabled={saving}
                        onClick={() => void restore(revision.id, revision.revision_number)}
                      />
                    ) : null}
                  </div>
                ))}
                {!detail.can_restore ? (
                  <p className='cms-note'>Request changes before restoring a revision.</p>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      {!loading && tab === 'packages' ? (
        <div className='cms-packages'>
          {packages.map((item) => (
            <div className='cms-package' key={item.key}>
              <strong>
                {item.name} · {item.price_display}
              </strong>
              <p className='cms-meta'>
                {item.active ? 'Active' : 'Inactive'} · {item.cta_url}
                {item.live_slugs.length > 0
                  ? ` · Live on ${item.live_slugs.join(', ')}`
                  : ''}
                {item.referenced_slugs.length > 0
                  ? ` · Used by ${item.referenced_slugs.join(', ')}`
                  : ' · Unused'}
              </p>
              <div className='cms-actions'>
                <Button text='Edit' isSecondary onClick={() => openPackage(item)} />
                <Button
                  text='Delete'
                  variant='delete'
                  onClick={() => void removePackage(item)}
                />
              </div>
            </div>
          ))}
        </div>
      ) : null}
      {!loading && (tab === 'topics' || tab === 'posts' || tab === 'shared') ? (
        <EditorialCms section={tab} />
      ) : null}
      <CreatePageModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async (page) => {
          setCreateOpen(false);
          await load(page.slug);
          setTab('pages');
        }}
      />
      <PackageModal
        isOpen={packageOpen}
        packageEditing={packageEditing}
        onClose={() => setPackageOpen(false)}
        onSaved={async () => {
          setPackageOpen(false);
          await load(detail?.slug);
        }}
      />
    </div>
  );
};

const CreatePageModal = (props: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (page: CmsPageDetail) => Promise<void>;
}) => {
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const create = async () => {
    setSaving(true);
    try {
      const page = await createCmsPage({
        slug: slug.trim(),
        title: title.trim(),
        meta_title: metaTitle.trim(),
        meta_description: metaDescription.trim(),
        content: EMPTY_DOCUMENT,
      });
      toast.success('Draft created');
      await props.onCreated(page);
      setSlug('');
      setTitle('');
      setMetaTitle('');
      setMetaDescription('');
    } catch (error) {
      toast.error(cmsErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} title='New page' width='560px'>
      <div className='cms-fields'>
        <TextInput name='slug' title='Slug' value={slug} onChange={(event) => setSlug(event.target.value)} />
        <TextInput name='title' title='Title' value={title} onChange={(event) => setTitle(event.target.value)} />
        <TextInput
          name='meta_title'
          title='Meta title'
          value={metaTitle}
          onChange={(event) => setMetaTitle(event.target.value)}
        />
        <TextArea
          name='meta_description'
          title='Meta description'
          value={metaDescription}
          onChange={(event) => setMetaDescription(event.target.value)}
        />
        <Button text='Create draft' isDisabled={saving} onClick={() => void create()} />
      </div>
    </Modal>
  );
};

const PackageModal = (props: {
  isOpen: boolean;
  packageEditing: CmsPackage | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) => {
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [strapline, setStrapline] = useState('');
  const [inclusions, setInclusions] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const item = props.packageEditing;
    setKey(item?.key ?? '');
    setName(item?.name ?? '');
    setPrice(item?.price_display ?? '');
    setStrapline(item?.strapline ?? '');
    setInclusions(item?.inclusions.join('\n') ?? '');
    setCtaLabel(item?.cta_label ?? '');
    setCtaUrl(item?.cta_url ?? '');
    setActive(item?.active ?? true);
  }, [props.packageEditing, props.isOpen]);

  const save = async () => {
    const body = {
      name: name.trim(),
      price_display: price.trim(),
      strapline: strapline.trim(),
      inclusions: inclusions.split('\n').map((item) => item.trim()).filter((item) => item.length > 0),
      cta_label: ctaLabel.trim(),
      cta_url: ctaUrl.trim(),
      active,
    };
    if (props.packageEditing && !active && props.packageEditing.referenced_slugs.length > 0) {
      toast.error(`Package is used by: ${props.packageEditing.referenced_slugs.join(', ')}`);
      return;
    }
    if (props.packageEditing && props.packageEditing.live_slugs.length > 0) {
      const pages = props.packageEditing.live_slugs.join(', ');
      if (!window.confirm(`Saving updates the live site on: ${pages}`)) return;
    }
    setSaving(true);
    try {
      if (props.packageEditing) {
        await updateCmsPackage(props.packageEditing.key, body);
      } else {
        await createCmsPackage({ key: key.trim(), ...body });
      }
      toast.success('Package saved');
      await props.onSaved();
    } catch (error) {
      toast.error(cmsErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={props.packageEditing ? 'Edit package' : 'New package'}
      width='640px'
    >
      <div className='cms-fields'>
        <TextInput
          name='key'
          title='Key'
          value={key}
          isDisabled={props.packageEditing !== null}
          onChange={(event) => setKey(event.target.value)}
        />
        <TextInput name='name' title='Name' value={name} onChange={(event) => setName(event.target.value)} />
        <TextInput
          name='price_display'
          title='Price'
          value={price}
          onChange={(event) => setPrice(event.target.value)}
        />
        <TextArea
          name='strapline'
          title='Strapline'
          value={strapline}
          onChange={(event) => setStrapline(event.target.value)}
        />
        <label>
          <span className='cms-meta'>Inclusions, one per line</span>
          <textarea
            className='cms-inclusions'
            rows={6}
            value={inclusions}
            onChange={(event) => setInclusions(event.target.value)}
          />
        </label>
        <TextInput
          name='cta_label'
          title='Button label'
          value={ctaLabel}
          onChange={(event) => setCtaLabel(event.target.value)}
        />
        <TextInput
          name='cta_url'
          title='Button link'
          value={ctaUrl}
          placeholder='/how-it-works/'
          onChange={(event) => setCtaUrl(event.target.value)}
        />
        <label className='cms-check'>
          <input
            type='checkbox'
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
          />
          Active
        </label>
        <Button text='Save package' isDisabled={saving} onClick={() => void save()} />
      </div>
    </Modal>
  );
};

export default MarketingCmsPage;
